/**
 * Product Upsells Component
 * Handles toggle mode upsells that should be added with the main product
 */

class ProductUpsells {
  constructor(container) {
    this.container = container;
    this.mode = this.container.dataset.upsellMode;
    this.isProcessing = false;
    
    if (this.mode === 'toggle') {
      this.init();
    }
  }

  init() {
    // Find the product form
    this.productForm = this.container.closest('[data-section-type="product"]')?.querySelector('form[id^="AddToCartForm"]');
    
    if (!this.productForm) {
      console.warn('Product form not found for upsell toggles');
      return;
    }

    // Mark form as having upsell handling
    this.productForm.dataset.hasUpsellHandler = 'true';

    // Listen for form submission in CAPTURE phase to run before theme.AjaxProduct
    // Use stopImmediatePropagation to prevent other listeners from running
    this.productForm.addEventListener('submit', this.handleFormSubmit.bind(this), true);
  }

  handleFormSubmit(evt) {
    // Prevent duplicate processing
    if (this.isProcessing) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      return;
    }

    const checkedUpsells = this.getCheckedUpsells();
    
    if (checkedUpsells.length === 0) {
      // No upsells selected, let form submit normally
      return;
    }

    // Prevent all form submission handlers
    evt.preventDefault();
    evt.stopPropagation();
    evt.stopImmediatePropagation();

    // Add main product and upsells together
    this.addProductsToCart(checkedUpsells);
  }

  getCheckedUpsells() {
    const checkboxes = this.container.querySelectorAll('.js-upsell-toggle:checked');
    return Array.from(checkboxes).map(checkbox => ({
      id: checkbox.dataset.variantId,
      quantity: 1
    }));
  }

  async addProductsToCart(upsells) {
    const addToCartBtn = this.productForm.querySelector('[data-add-to-cart]');
    
    if (!addToCartBtn || this.isProcessing) return;

    // Set processing flag
    this.isProcessing = true;

    // Show loading state
    addToCartBtn.classList.add('is-loading');
    addToCartBtn.disabled = true;

    try {
      // Get main product form data
      const formData = new FormData(this.productForm);
      const mainProductId = formData.get('id');
      const mainProductQuantity = formData.get('quantity') || 1;

      // Build items array with main product + upsells - ensure IDs are integers
      const items = [
        {
          id: parseInt(mainProductId),
          quantity: parseInt(mainProductQuantity)
        },
        ...upsells.map(upsell => ({
          id: parseInt(upsell.id),
          quantity: parseInt(upsell.quantity)
        }))
      ];

      // Add all items at once using the cart/add.js endpoint
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items })
      });

      const data = await response.json();

      if (data.status === 422 || !response.ok) {
        this.handleError(data);
      } else {
        this.handleSuccess(data);
      }
    } catch (error) {
      console.error('Error adding products to cart:', error);
      this.handleError({ description: 'An error occurred. Please try again.' });
    } finally {
      // Remove loading state and reset processing flag
      addToCartBtn.classList.remove('is-loading');
      addToCartBtn.disabled = false;
      this.isProcessing = false;
    }
  }

  handleSuccess(data) {
    // Remove any existing errors
    const errors = this.productForm.querySelector('.errors');
    if (errors) {
      errors.remove();
    }

    // Dispatch success event for cart drawer to open
    this.productForm.dispatchEvent(new CustomEvent('ajaxProduct:added', {
      detail: {
        product: data.items ? data.items[0] : data,
        addToCartBtn: this.productForm.querySelector('[data-add-to-cart]')
      },
      bubbles: true
    }));

    // Reload if on cart page
    if (document.body.classList.contains('template-cart')) {
      window.scrollTo(0, 0);
      location.reload();
    }
  }

  handleError(error) {
    const errorMessage = error.description || error.message || 'An error occurred';
    
    // Remove existing errors
    const existingErrors = this.productForm.querySelector('.errors');
    if (existingErrors) {
      existingErrors.remove();
    }

    // Create error div
    const errorDiv = document.createElement('div');
    errorDiv.classList.add('errors', 'text-center');
    errorDiv.textContent = errorMessage;
    this.productForm.append(errorDiv);

    // Dispatch error event
    this.productForm.dispatchEvent(new CustomEvent('ajaxProduct:error', {
      detail: { errorMessage },
      bubbles: true
    }));
  }
}

// Initialize all product upsell components
function initProductUpsells(container = document) {
  container.querySelectorAll('.component-product-upsells[data-upsell-mode="toggle"]').forEach(element => {
    if (!element.dataset.upsellsInitialized) {
      element.dataset.upsellsInitialized = 'true';
      new ProductUpsells(element);
    }
  });
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  initProductUpsells();
});

// Re-initialize on Shopify section load (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    initProductUpsells(event.target);
  });
}
